import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AssistantProvider } from "@/contexts/AssistantContext";
import { AssistantBubble } from "@/components/assistant/AssistantBubble";
import { StaffAlerts } from "@/components/support/StaffAlerts";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import ProtectedRoute from "@/components/ProtectedRoute";
import { lazy, Suspense } from "react";

const Messages = lazy(() => import("@/pages/Messages"));
const PracticePage = lazy(() => import("@/pages/PracticePage"));
const TeacherInbox = lazy(() => import("@/pages/teacher/TeacherInbox"));
const AdminTeachers = lazy(() => import("@/pages/admin/Teachers"));
const AdminSupportInbox = lazy(() => import("@/pages/admin/SupportInbox"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Shop = lazy(() => import("@/pages/Shop"));
const Product = lazy(() => import("@/pages/Product"));
const Library = lazy(() => import("@/pages/Library"));
const LibraryReader = lazy(() => import("@/pages/LibraryReader"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Account = lazy(() => import("@/pages/Account"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Catalogue = lazy(() => import("@/pages/Catalogue"));
const About = lazy(() => import("@/pages/About"));
const Blog = lazy(() => import("@/pages/Blog"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const Ebooks = lazy(() => import("@/pages/Ebooks"));
const ArabeCoranique = lazy(() => import("@/pages/ArabeCoranique"));
const SourateLessons = lazy(() => import("@/pages/SourateLessons"));
const LessonDetail = lazy(() => import("@/pages/LessonDetail"));
const ExamPage = lazy(() => import("@/pages/ExamPage"));
const PlacementTest = lazy(() => import("@/pages/PlacementTest"));
const GrammarHome = lazy(() => import("@/pages/GrammarHome"));
const FinalExamPage = lazy(() => import("@/pages/FinalExamPage"));
const AdminGrammar = lazy(() => import("@/pages/admin/Grammar"));
const AdminAudioLevels = lazy(() => import("@/pages/admin/AudioLevels"));
const AudioCourses = lazy(() => import("@/pages/AudioCourses"));
const AudioExamPage = lazy(() => import("@/pages/AudioExamPage"));
const Profiles = lazy(() => import("@/pages/Profiles"));
const FamilyDashboard = lazy(() => import("@/pages/FamilyDashboard"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminEbooks = lazy(() => import("@/pages/admin/Ebooks"));
const AdminReminders = lazy(() => import("@/pages/admin/Reminders"));
const AdminQuran = lazy(() => import("@/pages/admin/Quran"));
const AdminQuranEditor = lazy(() => import("@/pages/admin/QuranEditor"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminUserFamily = lazy(() => import("@/pages/admin/UserFamily"));
const ProductForm = lazy(() => import("@/pages/admin/ProductForm"));
const AdminCategories = lazy(() => import("@/pages/admin/Categories"));
const AdminSales = lazy(() => import("@/pages/admin/Sales"));
const AdminCustomers = lazy(() => import("@/pages/admin/Customers"));
const CustomerDetail = lazy(() => import("@/pages/admin/CustomerDetail"));
const AdminMessages = lazy(() => import("@/pages/admin/Messages"));
const AdminShops = lazy(() => import("@/pages/admin/Shops"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));

const PageFallback = () => <div className="min-h-screen" style={{ background: "#0B0F1A" }} />;

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <AssistantProvider>
                <BrowserRouter>
                    <Suspense fallback={<PageFallback />}><Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/catalogue" element={<Catalogue />} />
                        <Route path="/a-propos" element={<About />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/rappel" element={<Blog />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/confidentialite" element={<Privacy />} />
                        <Route path="/conditions" element={<Terms />} />
                        <Route path="/abonnement" element={<Subscribe />} />
                        <Route path="/abonnement/succes" element={<SubscriptionSuccess />} />
                        <Route path="/ebooks" element={<Ebooks />} />
                        <Route path="/arabe-coranique" element={<ArabeCoranique />} />
                        <Route path="/grammaire" element={<GrammarHome />} />
                        <Route path="/examen-final/grammaire/:m" element={<ProtectedRoute><FinalExamPage kind="grammar" /></ProtectedRoute>} />
                        <Route path="/examen-final/audio/:sid/:lid" element={<ProtectedRoute><FinalExamPage kind="audio" /></ProtectedRoute>} />
                        <Route path="/arabe-coranique/test-de-niveau" element={<ProtectedRoute><PlacementTest /></ProtectedRoute>} />
                        <Route path="/arabe-coranique/sourate/:surahNumber" element={<SourateLessons />} />
                        <Route path="/arabe-coranique/cours/:lessonId" element={<LessonDetail />} />
                        <Route path="/arabe-coranique/cours/:lessonId/examen" element={<ExamPage />} />
                        <Route path="/boutique/:slug" element={<Shop />} />
                        <Route path="/produit/:id" element={<Product />} />
                        <Route path="/checkout/success" element={<CheckoutSuccess />} />
                        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                        <Route path="/profils" element={<ProtectedRoute requireProfile={false}><Profiles /></ProtectedRoute>} />
                        <Route path="/famille" element={<ProtectedRoute><FamilyDashboard /></ProtectedRoute>} />
                        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
                        <Route path="/library/:productId" element={<ProtectedRoute><LibraryReader /></ProtectedRoute>} />
                        <Route path="/library/:productId/examen" element={<ProtectedRoute><AudioExamPage /></ProtectedRoute>} />
                        <Route path="/cours-audio" element={<AudioCourses />} />
                        <Route path="/arabe-coranique/cours/:lessonId/entrainement" element={<ProtectedRoute><PracticePage kind="quran" /></ProtectedRoute>} />
                        <Route path="/library/:productId/entrainement" element={<ProtectedRoute><PracticePage kind="audio" /></ProtectedRoute>} />
                        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                        <Route path="/messages/:ticketId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                        <Route path="/prof" element={<ProtectedRoute roles={["teacher"]}><TeacherInbox /></ProtectedRoute>} />
                        <Route path="/prof/:ticketId" element={<ProtectedRoute roles={["teacher"]}><TeacherInbox /></ProtectedRoute>} />

                        <Route element={<ProtectedRoute roles={["super_admin", "shop_admin"]}><AdminLayout /></ProtectedRoute>}>
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/admin/products" element={<AdminProducts />} />
                            <Route path="/admin/products/new" element={<ProductForm />} />
                            <Route path="/admin/products/:id/edit" element={<ProductForm />} />
                            <Route path="/admin/ebooks" element={<AdminEbooks />} />
                            <Route path="/admin/ebooks/new" element={<ProductForm defaultType="ebook" />} />
                            <Route path="/admin/reminders" element={<AdminReminders />} />
                            <Route path="/admin/quran" element={<AdminQuran />} />
                            <Route path="/admin/quran/cours/:lessonId" element={<AdminQuranEditor />} />
                            <Route path="/admin/grammaire" element={<AdminGrammar />} />
                            <Route path="/admin/cours-audio" element={<AdminAudioLevels />} />
                            <Route path="/admin/users" element={<AdminUsers />} />
                            <Route path="/admin/users/:userId/suivi" element={<AdminUserFamily />} />
                            <Route path="/admin/categories" element={<AdminCategories />} />
                            <Route path="/admin/sales" element={<AdminSales />} />
                            <Route path="/admin/customers" element={<AdminCustomers />} />
                            <Route path="/admin/customers/:userId" element={<CustomerDetail />} />
                            <Route path="/admin/messages" element={<AdminMessages />} />
                            <Route path="/admin/support" element={<AdminSupportInbox />} />
                            <Route path="/admin/support/:ticketId" element={<AdminSupportInbox />} />
                            <Route path="/admin/professeurs" element={<AdminTeachers />} />
                            <Route path="/admin/shops" element={<ProtectedRoute roles={["super_admin"]}><AdminShops /></ProtectedRoute>} />
                            <Route path="/admin/settings" element={<ProtectedRoute roles={["super_admin"]}><AdminSettings /></ProtectedRoute>} />
                        </Route>
                    </Routes></Suspense>
                    <AssistantBubble />
                    <StaffAlerts />
                </BrowserRouter>
                <Toaster richColors />
                </AssistantProvider>
            </AuthProvider>
        </div>
    );
}
